<template>
  <div class="ask-clara-footer">
    <button
      class="ask-clara-footer__button"
      :class="{ 'ask-clara-footer__button--active': claraOpen }"
      @click="toggle"
    >
      <svg
        class="ask-clara-footer__icon"
        width="16"
        height="16"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 2.2 14.5 7.4c.7.3.6 1.3-.1 1.5l-4.7 1.3-1.3 4.7c-.2.7-1.2.8-1.5.1L1.7 3.5C1.4 2.8 2.3 1.9 3 2.2Z"
          fill="currentColor"
        />
      </svg>
      <span class="ask-clara-footer__label">{{ $t('adminTitles.askClara.ask') }}</span>
      <span class="ask-clara-footer__hint">{{ shortcutHint }}</span>
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

// Service-hub routes → SERVICE; everything else (leads, default) → SALES.
const SERVICE_HUB_PREFIXES = ['/admin/policies', '/admin/payments', '/admin/renewals', '/admin/claims'];

export default {
  name: 'AskClaraFooter',
  computed: {
    ...mapGetters('Clara', ['claraOpen']),
    ...mapGetters(['providerType']),
    // Show the platform-correct shortcut: ⌘K on macOS, Ctrl K elsewhere (Windows/Linux).
    shortcutHint() {
      const platform = navigator.userAgentData?.platform || navigator.platform || '';
      const isMac = /mac|iphone|ipad|ipod/i.test(platform);
      return isMac ? '⌘K' : 'Ctrl K';
    },
    // Derived here (not in the panel) because the footer is always mounted, so the
    // store stays in sync with the route before the panel opens and fires its loaders.
    // Hubs the current page spans → /suggestions and /today (the backend gates each
    // hub by subscription + permission, so it's safe to request both on shared pages).
    hubs() {
      const path = this.$route.path || '';
      if (SERVICE_HUB_PREFIXES.some((prefix) => path.startsWith(prefix))) return ['SERVICE'];
      if (path.startsWith('/admin/leads')) return ['SALES'];
      // Non-hub / shared pages (dashboard, users, …) span both.
      return ['SALES', 'SERVICE'];
    },
    // Single hub for /ask, which still takes a scalar. On a shared page we can't send
    // "both", so fall back to the tenant type to pick the most likely one.
    hub() {
      if (this.hubs.length === 1) return this.hubs[0];
      return this.providerType === 'service_hub' ? 'SERVICE' : 'SALES';
    },
  },
  watch: {
    hubs: {
      immediate: true,
      handler(value) {
        this.$store.commit('Clara/SET_HUBS', value);
        this.$store.commit('Clara/SET_HUB', this.hub);
      },
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    toggle() {
      if (this.claraOpen) {
        this.$store.dispatch('Clara/closePanel');
      } else {
        this.$store.dispatch('Clara/openPanel');
      }
    },
    handleKeydown(e) {
      // ⌘K / Ctrl+K toggles the overlay; esc closes it when open.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        this.toggle();
      } else if (e.key === 'Escape' && this.claraOpen) {
        this.$store.dispatch('Clara/closePanel');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ask-clara-footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 40px;
  padding: 0 48px;
  border-top: 1px solid var(--solid-lavender-grey);
  background: #FFFFFF;

  &__button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: none;
    background: transparent;
    padding: 4px 8px;
    margin-right: -8px;
    border-radius: 8px;
    cursor: pointer;
    color: var(--solid-purple);
    transition: background 0.15s ease;

    &:hover {
      background: var(--solid-light-purple, #F4F2FB);
    }

    &--active {
      background: var(--solid-light-purple, #F4F2FB);
    }
  }

  &__icon {
    flex-shrink: 0;
  }

  &__label {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 13px;
    line-height: 16px;
    color: var(--solid-black-purple);
  }

  &__hint {
    font-size: 11px;
    font-weight: 500;
    color: var(--solid-grey, #6B6B80);
    border: 1px solid var(--solid-lavender-grey);
    border-radius: 5px;
    padding: 1px 5px;
  }
}

@media screen and (max-width: 668px) {
  .ask-clara-footer {
    padding: 0 20px;
  }

  .ask-clara-footer__hint {
    display: none;
  }
}
</style>
